/* ============================================================================
   gp-quiz-track.js — client for the gp-quiz event pipeline.

   Why this exists: Klaviyo's onsite JS buffers events for unidentified
   visitors in localStorage and only pushes them once that visitor identifies
   themselves. Anyone who abandons before the email step therefore leaves no
   trace at all, which made pre-email drop-off structurally invisible. This
   client posts to our own endpoint instead, so an abandoner at question three
   is recorded the moment they leave.

   Contract:
     GPQuizTrack.init({ endpoint, quiz, version, lp_code?, lp_qa? })
     GPQuizTrack.track(name, props)
     GPQuizTrack.flush()

   lp_code / lp_qa (2026-10-01): the A/B test arm picked by
   snippets/gpx-experiment.liquid. When set, every batch carries them as `lp`
   and `lpq`; pages without a test send exactly what they sent before.

   Guarantees:
     - Every event carries a monotonic `seq`; the server dedupes on
       `<session_id>:<seq>`, so retried beacons are harmless.
     - The queue survives a reload (sessionStorage), so a failed final beacon
       is re-sent on the next page load rather than lost.
     - Nothing here can throw into the quiz. Every entry point is wrapped.
   ========================================================================== */
(function (w, d) {
  'use strict';

  var FLUSH_MS = 4000;
  var FLUSH_SIZE = 12;
  /* Sent immediately: these are the events we cannot afford to batch. */
  var URGENT = { email_captured: 1, quiz_completed: 1, add_to_cart: 1 };

  var cfg = null;
  var queue = [];
  var seq = 0;
  var sessionId = '';
  var anonId = '';
  var ctxSent = false;
  var timer = null;

  function uuid() {
    if (w.crypto && w.crypto.randomUUID) return w.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function get(store, k) { try { return store.getItem(k); } catch (e) { return null; } }
  function set(store, k, v) { try { store.setItem(k, v); } catch (e) {} }

  function device() {
    var ua = navigator.userAgent || '';
    if (/iPad|Tablet/i.test(ua)) return 'tablet';
    return /Mobi|Android|iPhone/i.test(ua) ? 'mobile' : 'desktop';
  }

  /* Each ad network appends its own click id to the landing URL, and only one
     of them is ever present. Order is precedence for the pathological case
     where a link carries more than one.

     TikTok's ttclid is auto-appended on every ad click and is the value its
     Events API matches a conversion against. It expires with the campaign's
     CTA window and cannot be recovered after the fact, so it is stored now
     even though nothing delivers to TikTok yet. */
  var CLICK_PARAMS = ['fbclid', 'ttclid', 'gclid'];

  function clickRef(p) {
    for (var i = 0; i < CLICK_PARAMS.length; i++) {
      var v = p.get(CLICK_PARAMS[i]);
      if (v) return { id: v, source: CLICK_PARAMS[i] };
    }
    return { id: '', source: '' };
  }

  /* Acquisition context, sent once per session with the first batch. */
  function acquisitionContext() {
    var p = new URLSearchParams(w.location.search);
    var click = clickRef(p);
    return {
      landing_path: w.location.pathname,
      referrer: d.referrer || '',
      utm_source: p.get('utm_source') || '',
      utm_medium: p.get('utm_medium') || '',
      utm_campaign: p.get('utm_campaign') || '',
      utm_content: p.get('utm_content') || '',
      utm_term: p.get('utm_term') || '',
      click_id: click.id,
      click_source: click.source,
      device: device()
    };
  }

  function queueKey() { return 'gpq_queue_' + cfg.quiz; }

  function persistQueue() { set(w.sessionStorage, queueKey(), JSON.stringify(queue)); }

  function payload(events) {
    var body = {
      session_id: sessionId,
      anon_id: anonId,
      quiz: cfg.quiz,
      v: cfg.version,
      events: events
    };
    if (cfg.lp_code) { body.lp = cfg.lp_code; body.lpq = cfg.lp_qa ? 1 : 0; }
    if (!ctxSent) body.ctx = acquisitionContext();
    return JSON.stringify(body);
  }

  /* `useBeacon` is set on pagehide, where fetch is unreliable but sendBeacon
     is still delivered by the browser after the document goes away. */
  function send(events, useBeacon) {
    if (!events.length) return;
    var body = payload(events);
    ctxSent = true;

    if (useBeacon && navigator.sendBeacon) {
      /* text/plain avoids a CORS preflight, which a beacon cannot survive. */
      var ok = navigator.sendBeacon(cfg.endpoint, new Blob([body], { type: 'text/plain' }));
      if (ok) { queue = []; persistQueue(); }
      return;
    }

    fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: body,
      keepalive: true,
      mode: 'cors'
    }).then(function (r) {
      if (r && r.ok) { queue = []; persistQueue(); }
    })['catch'](function () { /* leave queued; retried on next flush or load */ });
  }

  function flush(useBeacon) {
    if (!cfg || !queue.length) return;
    if (timer) { clearTimeout(timer); timer = null; }
    send(queue.slice(), useBeacon);
  }

  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; flush(false); }, FLUSH_MS);
  }

  var API = {
    init: function (options) {
      try {
        /* one tracker per page: a second init for the same quiz is a no-op */
        if (cfg && cfg.quiz === options.quiz) return;
        cfg = {
          endpoint: options.endpoint,
          quiz: options.quiz,
          version: options.version || 0,
          lp_code: options.lp_code || '',
          lp_qa: options.lp_qa ? 1 : 0
        };

        anonId = get(w.localStorage, 'gpq_anon') || uuid();
        set(w.localStorage, 'gpq_anon', anonId);

        /* sessionStorage: one session per tab visit, surviving reloads. */
        var sKey = 'gpq_sid_' + cfg.quiz;
        sessionId = get(w.sessionStorage, sKey) || uuid();
        set(w.sessionStorage, sKey, sessionId);

        seq = parseInt(get(w.sessionStorage, 'gpq_seq_' + cfg.quiz) || '0', 10) || 0;
        ctxSent = get(w.sessionStorage, 'gpq_ctx_' + cfg.quiz) === '1';

        /* Re-send anything a previous page load failed to deliver. */
        var stale = get(w.sessionStorage, queueKey());
        if (stale) {
          try { queue = JSON.parse(stale) || []; } catch (e) { queue = []; }
          if (queue.length) flush(false);
        }

        d.addEventListener('visibilitychange', function () {
          if (d.visibilityState === 'hidden') flush(true);
        });
        w.addEventListener('pagehide', function () { flush(true); });
      } catch (e) { cfg = null; }
    },

    track: function (name, props) {
      try {
        if (!cfg) return;
        props = props || {};
        seq += 1;
        set(w.sessionStorage, 'gpq_seq_' + cfg.quiz, String(seq));

        var e = { seq: seq, name: name, ts: Date.now() };
        if (props.screen_id != null) e.screen_id = String(props.screen_id);
        if (props.screen_type != null) e.screen_type = String(props.screen_type);
        if (props.step_index != null) e.step_index = props.step_index | 0;

        var rest = {};
        for (var k in props) {
          if (k !== 'screen_id' && k !== 'screen_type' && k !== 'step_index') rest[k] = props[k];
        }
        if (Object.keys(rest).length) e.props = rest;

        queue.push(e);
        persistQueue();
        if (ctxSent) set(w.sessionStorage, 'gpq_ctx_' + cfg.quiz, '1');

        if (URGENT[name] || queue.length >= FLUSH_SIZE) flush(false);
        else schedule();
      } catch (err) { /* tracking must never break the quiz */ }
    },

    flush: function () { try { flush(false); } catch (e) {} },

    /** Exposed for debugging in the console. */
    debug: function () {
      return { sessionId: sessionId, anonId: anonId, seq: seq, queued: queue.length, cfg: cfg };
    }
  };

  w.GPQuizTrack = API;
})(window, document);
